<template>
  <div class="period-grouped-list">
    <template v-for="(group, index) in groupedItems" :key="index">
      <UiTimeGroupSeparator :label="group.label" />
      <div class="group-items">
        <component 
          v-for="item in group.items" 
          :key="item.key" 
          :is="item.component" 
          v-bind="item.props" 
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import type { VNode } from 'vue'

interface Props {
  fieldPeriod?: string
}

const props = withDefaults(defineProps<Props>(), {
  fieldPeriod: 'group_period'
})

const slots = useSlots()

interface GroupedItem {
  component: any
  props: any
  key: string | number
  period: string
}

interface PeriodGroup {
  label: string
  items: GroupedItem[]
  sortOrder: number
}

// Маппинг значений group_period в отображаемые метки и порядок сортировки
const getPeriodGroup = (period: string): { label: string, sortOrder: number } => {
  const periodMapping: Record<string, { label: string, sortOrder: number }> = {
    'today': { label: 'Today', sortOrder: 0 },
    'yesterday': { label: 'Yesterday', sortOrder: 1 },
    'week': { label: 'This week', sortOrder: 2 },
    'month': { label: 'This month', sortOrder: 3 },
    '3months': { label: '3 months ago', sortOrder: 4 },
    'earlier': { label: 'Earlier', sortOrder: 5 }
  }
  
  return periodMapping[period] || { label: period, sortOrder: 999 }
}

// Извлекаем элементы из слота и группируем их по периоду
const groupedItems = computed<PeriodGroup[]>(() => {
  const defaultSlot = slots.default?.()
  if (!defaultSlot) return []
  
  // Извлекаем все элементы с их периодом
  const items: GroupedItem[] = []
  
  const processVNodes = (vnodes: VNode[]) => {
    vnodes.forEach((vnode, index) => {
      if (vnode.type === 'template' || vnode.type === Symbol.for('v-fgt')) {
        // Обрабатываем фрагменты
        if (Array.isArray(vnode.children)) {
          processVNodes(vnode.children as VNode[])
        }
      } else if (vnode.props && typeof vnode.props === 'object') {
        // Ищем указанное поле периода в пропсах
        let period: string | null = null
        
        // Проверяем различные возможные места где может быть период
        if (vnode.props.notification?.[props.fieldPeriod]) {
          period = vnode.props.notification[props.fieldPeriod]
        } else if (vnode.props.group?.[props.fieldPeriod]) {
          period = vnode.props.group[props.fieldPeriod]
        } else if (vnode.props[props.fieldPeriod]) {
          period = vnode.props[props.fieldPeriod]
        } else if (vnode.props[props.fieldPeriod.replace(/([A-Z])/g, '-$1').toLowerCase()]) {
          // Поддержка kebab-case версии prop
          period = vnode.props[props.fieldPeriod.replace(/([A-Z])/g, '-$1').toLowerCase()]
        }
        
        if (period) {
          items.push({
            component: vnode,
            props: vnode.props,
            key: (typeof vnode.key === 'string' || typeof vnode.key === 'number') ? vnode.key : `item-${index}`,
            period: period
          })
        }
      }
    })
  }
  
  processVNodes(defaultSlot)
  
  // Группируем по периодам
  const groups = new Map<string, PeriodGroup>()
  
  items.forEach(item => {
    const periodGroup = getPeriodGroup(item.period)
    const key = `${periodGroup.sortOrder}-${periodGroup.label}`
    
    if (!groups.has(key)) {
      groups.set(key, {
        label: periodGroup.label,
        items: [],
        sortOrder: periodGroup.sortOrder
      })
    }
    
    groups.get(key)!.items.push(item)
  })
  
  // Возвращаем группы отсортированные по sortOrder
  return Array.from(groups.values()).sort((a, b) => a.sortOrder - b.sortOrder)
})
</script>

<style scoped>
.period-grouped-list {
  display: flex;
  flex-direction: column;
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
</style>
